// src/features/readingnote/components/ReviewCardModal.tsx
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { QRCodeCanvas } from "qrcode.react";
import { toCoverUrl } from "@/lib/coverUrl";
import { reviewCardUpload } from "@/lib/api";
import { COLOR, FW, RADIUS } from "@/ui/tokens";

const DOWNLOAD_ICON = (import.meta.env.BASE_URL || "./") + "icons/card_download.svg";
const QR_ICON = (import.meta.env.BASE_URL || "./") + "icons/card_qr.svg";

const SAVE_TARGET = "media:Pictures/ReadersNote";
const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");

function toProxiedUrl(url: string): string {
    if (!url) return url;
    if (url.startsWith("data:")) return url;
    // 같은 origin(로고 등 정적 자산, 프록시된 /uploads)은 프록시하지 않는다.
    // img.src로 읽으면 절대 URL이라 startsWith("/")로는 걸러지지 않으므로 origin을 비교한다.
    try {
        if (new URL(url, window.location.href).origin === window.location.origin) return url;
    } catch { return url; }
    if (API_BASE && url.startsWith(API_BASE)) return url;      // 이미 API origin이면 그대로
    // 외부 이미지는 백엔드 프록시로 우회(CORS). dev(API_BASE="")에서는 상대경로가 되어 vite가 프록시.
    return `${API_BASE}/proxy-image?url=${encodeURIComponent(url)}`;
}

function StarIcon({ filled, size = 18 }: { filled: boolean; size?: number }) {
    const c = filled ? "#111" : "#D1D5DB";
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ display: "block" }}>
            <path
                d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                fill={c} stroke={c} strokeWidth={2.6} strokeLinejoin="round"
            />
        </svg>
    );
}

function fmtDate(iso: string) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return "";
        return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
    } catch { return ""; }
}

export interface ReviewCardData {
    id: number;
    content: string;
    rating: number;
    keywords: string[];
    created_at?: string;
    createdAt?: string;
    Book?: { title?: string; cover_url?: string };
}

interface Props {
    open: boolean;
    onClose: () => void;
    review: ReviewCardData;
    nickname: string;
}

export function ReviewCardModal({ open, onClose, review, nickname }: Props) {
    const cardRef = useRef<HTMLDivElement>(null);
    const [saving, setSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState<"idle" | "ok" | "err">("idle");
    const [qrLoading, setQrLoading] = useState(false);
    const [qrUrl, setQrUrl] = useState<string | null>(null);

    useEffect(() => {
        if (open) { setSaveStatus("idle"); setQrUrl(null); }
    }, [open]);

    if (!open) return null;

    const coverUrl = review.Book?.cover_url ? toCoverUrl(review.Book.cover_url) : "";
    const keywords = review.keywords || [];
    const rawDate = review.created_at || review.createdAt || "";
    const dateStr = fmtDate(rawDate);

    // 카드 DOM → PNG dataURL 캡처. (저장/QR 공용)
    // 외부이미지 프록시 교체 + Android 키워드 pill 우회 처리 후 html2canvas로 그린다.
    const captureCard = async (): Promise<string> => {
        const el = cardRef.current;
        if (!el) throw new Error("CARD_NOT_READY");

        // 1) 외부 이미지 → 프록시 URL 교체 (html2canvas가 useCORS로 직접 처리)
        const imgs = Array.from(el.querySelectorAll("img"));
        const origSrcs = imgs.map(img => img.src);
        imgs.forEach(img => { img.src = toProxiedUrl(img.src); });

        // 2) 키워드 pill → Canvas API로 미리 그린 이미지로 교체
        //    html2canvas가 폰트 베이스라인을 잘못 계산해 pill 텍스트가 세로 중앙에 안 맞는 버그 우회.
        //    캡처 중에만 교체했다가 finally에서 원복하므로 화면엔 순간적으로만 영향.
        const kwEls = Array.from(
            el.querySelectorAll("[data-kw-pill]")
        ) as HTMLElement[];
        const kwRestore: Array<{ parent: Node; span: HTMLElement; img: HTMLImageElement }> = [];

        const PAD_H = 7;
        const FONT_SIZE = 9;
        const DPR = 2;
        const fontFamily = kwEls.length > 0
            ? window.getComputedStyle(kwEls[0]).fontFamily
            : "'NanumSquare','NanumSquareOTF','NanumSquareAc',sans-serif";
        const FONT_STR = `600 ${FONT_SIZE}px ${fontFamily}`;

        for (const span of kwEls) {
            const text = span.textContent || "";
            const PILL_H = span.offsetHeight || 15;

            // 텍스트 너비 측정
            const mc = document.createElement("canvas");
            const mctx = mc.getContext("2d")!;
            mctx.font = FONT_STR;
            const pillW = Math.ceil(mctx.measureText(text).width) + PAD_H * 2;

            // pill 그리기 (흰 배경 + 테두리)
            const c = document.createElement("canvas");
            c.width = pillW * DPR;
            c.height = PILL_H * DPR;
            const ctx = c.getContext("2d")!;
            ctx.scale(DPR, DPR);

            // 0.5px inset so 1px stroke stays fully within canvas bounds
            const s = 0.5;
            const r = (PILL_H - 1) / 2;
            ctx.beginPath();
            ctx.moveTo(r + s, s);
            ctx.lineTo(pillW - r - s, s);
            ctx.arcTo(pillW - s, s, pillW - s, r + s, r);
            ctx.lineTo(pillW - s, PILL_H - r - s);
            ctx.arcTo(pillW - s, PILL_H - s, pillW - r - s, PILL_H - s, r);
            ctx.lineTo(r + s, PILL_H - s);
            ctx.arcTo(s, PILL_H - s, s, PILL_H - r - s, r);
            ctx.lineTo(s, r + s);
            ctx.arcTo(s, s, r + s, s, r);
            ctx.closePath();

            ctx.fillStyle = "#ffffff";
            ctx.fill();
            ctx.strokeStyle = "#333333";
            ctx.lineWidth = 1;
            ctx.stroke();

            ctx.fillStyle = "#222222";
            ctx.font = FONT_STR;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(text, pillW / 2, PILL_H / 2);

            const img = document.createElement("img");
            img.src = c.toDataURL("image/png");
            img.style.cssText = `width:${pillW}px;height:${PILL_H}px;display:inline-block;vertical-align:top;margin-right:4px;margin-bottom:4px;`;
            span.parentNode!.insertBefore(img, span);
            span.style.display = "none";
            kwRestore.push({ parent: span.parentNode!, span, img });
        }

        // 3) BOOK REVIEW 헤더 → Canvas로 재드로잉.
        //    html2canvas가 lineHeight:1 텍스트를 박스 위쪽에 붙여 그려 캡처에서 세로 중앙이
        //    안 맞는 문제 우회. 박스 크기 그대로 img로 교체(레이아웃 불변) 후 finally에서 복원.
        let titleRestore: (() => void) | null = null;
        const titleEl = el.querySelector("[data-card-title]") as HTMLElement | null;
        if (titleEl) {
            const w = titleEl.offsetWidth;
            const h = titleEl.offsetHeight;
            const cs = window.getComputedStyle(titleEl);
            const cv = document.createElement("canvas");
            cv.width = w * DPR;
            cv.height = h * DPR;
            const cx = cv.getContext("2d")!;
            cx.scale(DPR, DPR);
            cx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
            try { (cx as any).letterSpacing = cs.letterSpacing; } catch { /* older canvas */ }
            cx.fillStyle = cs.color;
            cx.textAlign = "center";
            cx.textBaseline = "middle";
            cx.fillText(titleEl.textContent || "", w / 2, h / 2);

            const timg = document.createElement("img");
            timg.src = cv.toDataURL("image/png");
            timg.style.cssText = `width:${w}px;height:${h}px;display:block;`;
            const prevHTML = titleEl.innerHTML;
            titleEl.innerHTML = "";
            titleEl.appendChild(timg);
            titleRestore = () => { titleEl.innerHTML = prevHTML; };
        }

        try {
            const { default: html2canvas } = await import("html2canvas");
            // 기기마다 카드 렌더링 크기가 달라도 항상 720×1280(9:16) 고정 해상도로 출력
            const TARGET_W = 720;
            const renderedW = el.offsetWidth || TARGET_W;
            const captureScale = TARGET_W / renderedW;
            const canvas = await html2canvas(el, {
                useCORS: true,
                allowTaint: false,
                scale: captureScale,
                backgroundColor: "#c8e9f5",
                logging: false,
            });
            return canvas.toDataURL("image/png");
        } finally {
            // 이미지 src 원복
            imgs.forEach((img, i) => { img.src = origSrcs[i]; });
            // 키워드 pill 원복
            for (const { parent, span, img } of kwRestore) {
                span.style.display = "";
                parent.removeChild(img);
            }
            // BOOK REVIEW 헤더 원복
            if (titleRestore) titleRestore();
        }
    };

    // 다운로드/앱 갤러리 저장
    const handleSave = async () => {
        if (saving || qrLoading) return;
        setSaving(true);
        setSaveStatus("idle");
        try {
            const dataUrl = await captureCard();
            const fileName = `review_card_${review.id || Date.now()}.png`;
            const android = (window as any).Android;
            if (android && typeof android.saveImageFromBase64 === "function") {
                android.saveImageFromBase64(dataUrl, fileName, SAVE_TARGET);
            } else {
                const a = document.createElement("a");
                a.href = dataUrl;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
            setSaveStatus("ok");
        } catch (err) {
            console.error("[ReviewCardModal] save error:", err);
            setSaveStatus("err");
        } finally {
            setSaving(false);
        }
    };

    // QR: 카드 이미지를 서버에 업로드하고 공개 URL로 QR 생성
    const handleQr = async () => {
        if (saving || qrLoading) return;
        setQrLoading(true);
        setSaveStatus("idle");
        try {
            const dataUrl = await captureCard();
            const blob = await (await fetch(dataUrl)).blob();
            const { url } = await reviewCardUpload(blob);
            // "/uploads/.." → 절대 URL. 프로덕션은 VITE_API_BASE로 절대경로가 되고,
            // dev/ngrok 등 API_BASE가 비어 상대경로면 현재 origin을 붙여 폰에서도 접근 가능하게 한다.
            let abs = toCoverUrl(url);
            if (abs.startsWith("/")) abs = window.location.origin + abs;
            setQrUrl(abs);
        } catch (err) {
            console.error("[ReviewCardModal] qr error:", err);
            setSaveStatus("err");
        } finally {
            setQrLoading(false);
        }
    };

    const modalRoot = typeof document !== "undefined" ? document.body : null;
    if (!modalRoot) return null;

    return ReactDOM.createPortal(
        /* DIM 배경 */
        <div
            style={{
                position: "fixed",
                top: 0, left: 0, right: 0, bottom: 0,
                width: "100%", height: "100%",
                background: "rgba(0,0,0,0.6)",
                zIndex: 9999,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                overflowY: "auto",
            }}
            onClick={onClose}
        >
            {/* QR 로딩 오버레이 */}
            {qrLoading && (
                <div style={{
                    position: "fixed", inset: 0, zIndex: 10000,
                    background: "rgba(0,0,0,0.5)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#fff", fontSize: 15, fontWeight: 700,
                }}>
                    QR 생성 중…
                </div>
            )}

            {/* QR 결과 오버레이 */}
            {qrUrl && (
                <div
                    onClick={(e) => { e.stopPropagation(); setQrUrl(null); }}
                    style={{
                        position: "fixed", inset: 0, zIndex: 10000,
                        background: "rgba(0,0,0,0.7)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 24, boxSizing: "border-box",
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: COLOR.white,
                            borderRadius: RADIUS.xl,
                            padding: "28px 24px 20px",
                            display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
                            maxWidth: 320, width: "100%",
                            boxShadow: "0 8px 40px rgba(0,0,0,0.35)",
                        }}
                    >
                        <span style={{ fontSize: 16, fontWeight: FW.extraBold, color: COLOR.text }}>QR로 저장</span>
                        <div style={{ padding: 12, background: "#fff", border: "1px solid #eee", borderRadius: RADIUS.md }}>
                            <QRCodeCanvas value={qrUrl} size={200} includeMargin={false} />
                        </div>
                        <span style={{ fontSize: 13, color: "#666", textAlign: "center", lineHeight: 1.5 }}>
                            휴대폰 카메라로 QR을 스캔하면<br />리뷰 카드 이미지를 받을 수 있어요.
                        </span>
                        <button
                            onClick={() => setQrUrl(null)}
                            style={{
                                marginTop: 4,
                                background: COLOR.text, color: COLOR.white,
                                border: "none", borderRadius: RADIUS.md,
                                padding: "8px 28px", fontSize: 15, fontWeight: FW.bold, cursor: "pointer",
                            }}
                        >
                            닫기
                        </button>
                    </div>
                </div>
            )}

            <div
                onClick={e => e.stopPropagation()}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: "20px 16px",
                    boxSizing: "border-box",
                    width: "100%",
                }}
            >
                {/* 흰색 통합 박스: 저장 버튼 + 카드 */}
                <div style={{
                    width: "100%",
                    maxWidth: 360,
                    background: COLOR.white,
                    borderRadius: RADIUS.xl,
                    overflow: "hidden",
                    boxShadow: "0 8px 40px rgba(0,0,0,0.35)",
                }}>
                    {/* 툴바 */}
                    <div style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 4,
                        padding: "16px 16px 12px",
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                            <span style={{ flex: 1, fontSize: 16, fontWeight: FW.extraBold, color: COLOR.text }}>리뷰 저장</span>
                            {saveStatus === "ok" && (
                                <span style={{ fontSize: 13, color: "#16a34a", fontWeight: 700 }}>저장 완료!</span>
                            )}
                            {saveStatus === "err" && (
                                <span style={{ fontSize: 13, color: "#dc2626", fontWeight: 700 }}>저장 실패</span>
                            )}
                            <button
                                onClick={handleQr}
                                disabled={saving || qrLoading}
                                style={{
                                    background: "transparent",
                                    border: "none",
                                    padding: 4,
                                    cursor: (saving || qrLoading) ? "default" : "pointer",
                                    opacity: (saving || qrLoading) ? 0.4 : 1,
                                    lineHeight: 0,
                                }}
                            >
                                <img src={QR_ICON} alt="QR" style={{ width: 28, height: 28, display: "block" }} />
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving || qrLoading}
                                style={{
                                    background: "transparent",
                                    border: "none",
                                    padding: 4,
                                    cursor: (saving || qrLoading) ? "default" : "pointer",
                                    opacity: (saving || qrLoading) ? 0.4 : 1,
                                    lineHeight: 0,
                                }}
                            >
                                <img src={DOWNLOAD_ICON} alt="저장" style={{ width: 28, height: 28, display: "block" }} />
                            </button>
                        </div>
                        {saveStatus === "ok" && (
                            <span style={{ fontSize: 11, color: "#aaa" }}>저장 위치: Pictures/ReadersNote/review_card_{review.id || "..."}.png</span>
                        )}
                    </div>

                    {/* 카드 미리보기 (html2canvas 캡처 대상) — 9:16 고정 270×480 */}
                    <div
                        ref={cardRef}
                        style={{
                            width: 270,
                            height: 480,
                            margin: "0 auto",
                            background: "#c8e9f5",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            padding: 18,
                            boxSizing: "border-box",
                            fontFamily: "inherit",
                            gap: 10,
                        }}
                    >
                        {/* 흰 카드 */}
                        <div style={{
                            width: "100%",
                            flex: 1,
                            minHeight: 0,
                            background: COLOR.white,
                            border: "1.5px solid #222",
                            borderRadius: 10,
                            display: "flex",
                            flexDirection: "column",
                            padding: "12px 14px 10px",
                            boxSizing: "border-box",
                            overflow: "hidden",
                        }}>
                            {/* BOOK REVIEW 헤더 */}
                            <div data-card-title="1" style={{
                                textAlign: "center",
                                fontSize: 15,
                                fontWeight: 900,
                                letterSpacing: "0.1em",
                                color: COLOR.text,
                                marginBottom: 8,
                                flexShrink: 0,
                                lineHeight: 1,
                            }}>
                                BOOK REVIEW
                            </div>

                            {/* 책 표지 */}
                            <div style={{
                                display: "flex",
                                justifyContent: "center",
                                marginBottom: 8,
                                flexShrink: 0,
                            }}>
                                {coverUrl ? (
                                    <img
                                        src={coverUrl}
                                        alt=""
                                        style={{ height: 100, maxWidth: "58%", objectFit: "contain", display: "block" }}
                                    />
                                ) : (
                                    <div style={{
                                        height: 100, width: 72,
                                        background: "#f3f4f6",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>
                                        <span style={{ color: "#9ca3af", fontSize: 9 }}>No Image</span>
                                    </div>
                                )}
                            </div>

                            {/* 별점 */}
                            <div style={{
                                display: "flex",
                                gap: 5,
                                justifyContent: "center",
                                marginBottom: 8,
                                flexShrink: 0,
                            }}>
                                {[1, 2, 3, 4, 5].map(n => (
                                    <StarIcon key={n} filled={n <= review.rating} size={20} />
                                ))}
                            </div>

                            {/* 구분선 */}
                            <div style={{ height: 1, background: "#333", marginBottom: 8, flexShrink: 0 }} />

                            {/* Book 섹션 */}
                            <div style={{ fontSize: 10, fontWeight: FW.extraBold, color: COLOR.text, marginBottom: 3, flexShrink: 0, lineHeight: 1 }}>Book</div>
                            <div style={{
                                fontSize: 10,
                                color: "#222",
                                paddingBottom: 4,
                                borderBottom: "1px solid #ccc",
                                marginBottom: 6,
                                lineHeight: 1.4,
                                flexShrink: 0,
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                            }}>
                                {review.Book?.title || "제목 없음"}
                            </div>

                            {/* Note 섹션 — flex:1 로 남은 공간 사용, 길어지면 clip */}
                            <div style={{ fontSize: 10, fontWeight: FW.extraBold, color: COLOR.text, marginBottom: 3, flexShrink: 0, lineHeight: 1 }}>Note</div>
                            <div style={{ flex: 1, minHeight: 0, overflow: "hidden", marginBottom: 6 }}>
                                <div style={{
                                    fontSize: 10,
                                    color: "#222",
                                    lineHeight: "18px",
                                    overflowWrap: "break-word",
                                    whiteSpace: "pre-line",
                                    backgroundImage: "repeating-linear-gradient(transparent, transparent 17px, #ccc 17px, #ccc 18px)",
                                    backgroundSize: "100% 18px",
                                    minHeight: "18px",
                                }}>
                                    {review.content || "\u00A0"}
                                </div>
                            </div>

                            {/* 키워드 — 흰 배경 + 테두리 */}
                            {keywords.length > 0 && (
                                // html2canvas 1.4.1은 flex gap을 무시하므로 inline-block + margin으로 배치
                                <div style={{
                                    marginBottom: 2,
                                    flexShrink: 0,
                                    lineHeight: 1,
                                }}>
                                    {keywords.map((k, i) => (
                                        <span key={i} data-kw-pill="1" style={{
                                            display: "inline-block",
                                            border: "1px solid #333",
                                            borderRadius: 999,
                                            paddingTop: 2,
                                            paddingBottom: 2,
                                            paddingLeft: 7,
                                            paddingRight: 7,
                                            marginRight: 4,
                                            marginBottom: 4,
                                            fontSize: 9,
                                            lineHeight: 1,
                                            fontWeight: 600,
                                            color: "#222",
                                            background: "#fff",
                                            whiteSpace: "nowrap",
                                            verticalAlign: "top",
                                        }}>
                                            {k}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* 하단: By 닉네임 | 날짜 */}
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexShrink: 0,
                            }}>
                                <span style={{ fontSize: 10, fontWeight: FW.extraBold, color: COLOR.text, lineHeight: 1 }}>
                                    By {nickname || "독자"}
                                </span>
                                {dateStr && (
                                    <span style={{ fontSize: 9, color: "#888", lineHeight: 1 }}>
                                        {dateStr.replace(/\./g, ". ")}
                                    </span>
                                )}
                            </div>

                        </div>

                        {/* 로고 */}
                        <div style={{ flexShrink: 0, display: "flex", justifyContent: "center" }}>
                            <img
                                src={(import.meta.env.BASE_URL || "./") + "icons/so_logo_b.svg"}
                                alt=""
                                style={{ height: 14, display: "block" }}
                            />
                        </div>
                    </div>

                </div>

                {/* 닫기 버튼 — 카드 바깥 아래 */}
                <button
                    onClick={onClose}
                    style={{
                        marginTop: 16,
                        width: "30%",
                        maxWidth: 360,
                        background: COLOR.text,
                        color: COLOR.white,
                        border: "none",
                        borderRadius: RADIUS.md,
                        padding: "8px 20px",
                        fontSize: 16,
                        fontWeight: FW.bold,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                    }}
                >
                    닫기
                </button>
            </div>
        </div>,
        modalRoot
    );
}
